import jsPDF from 'jspdf';

export const generateManagementFormPDF = async (form, practiceData) => {
  console.log('📄 Generating Management Form PDF:', form?.title);
  console.log('🏥 Practice data:', practiceData);
  
  try {
    const pdf = new jsPDF();
    let yPos = 20;
    const pageWidth = pdf.internal.pageSize.width;
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    
    // Extract practice information
    const practiceName = practiceData?.name || 'Dental Practice';
    
    // Format address properly (handle object or string)
    let practiceAddress = '';
    if (practiceData?.address) {
      if (typeof practiceData.address === 'object') {
        const addr = practiceData.address;
        practiceAddress = `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim();
      } else {
        practiceAddress = practiceData.address;
      }
    } else if (practiceData?.location) {
      practiceAddress = practiceData.location;
    }
    
    const practicePhone = practiceData?.phone || '';
    const practiceEmail = practiceData?.email || '';
    
    // Replace placeholders in form content
    let content = form.overview || '';
    content = content.replace(/{PracticeName}/g, practiceName);
    content = content.replace(/{PracticeAddress}/g, practiceAddress);
    content = content.replace(/{PracticePhone}/g, practicePhone);
    content = content.replace(/{PracticeEmail}/g, practiceEmail);
    
    // Add logo if available
    let logoAdded = false;
    if (practiceData?.branding?.logo) {
      try {
        const logoData = practiceData.branding.logo;
        let processedLogoData = logoData;
        
        if (!logoData.startsWith('data:image')) {
          processedLogoData = `data:image/png;base64,${logoData}`;
        }
        
        try {
          const imageProps = pdf.getImageProperties(processedLogoData);
          if (imageProps && imageProps.width && imageProps.height) {
            const aspectRatio = imageProps.width / imageProps.height;
            const maxLogoWidth = 60;
            const maxLogoHeight = 45;
            
            let finalWidth, finalHeight;
            if (aspectRatio > maxLogoWidth / maxLogoHeight) {
              finalWidth = maxLogoWidth;
              finalHeight = maxLogoWidth / aspectRatio;
            } else {
              finalHeight = maxLogoHeight;
              finalWidth = maxLogoHeight * aspectRatio;
            }
            
            const xPos = (pageWidth - finalWidth) / 2;
            pdf.addImage(processedLogoData, 'PNG', xPos, yPos, finalWidth, finalHeight);
            yPos += finalHeight + 10;
            logoAdded = true;
            console.log('✅ Logo added to form PDF');
          }
        } catch (dimensionError) {
          const imgWidth = 50;
          const imgHeight = 40;
          const xPos = (pageWidth - imgWidth) / 2;
          pdf.addImage(processedLogoData, 'PNG', xPos, yPos, imgWidth, imgHeight);
          yPos += imgHeight + 10;
          logoAdded = true;
        }
      } catch (logoError) {
        console.log('⚠️ Could not add logo:', logoError.message);
      }
    }
    
    // Add practice information header
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    
    // Center the practice name
    const practiceNameWidth = pdf.getTextWidth(practiceName);
    pdf.text(practiceName, (pageWidth - practiceNameWidth) / 2, yPos);
    yPos += 6;
    
    // Center practice info
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    if (practiceAddress) {
      const addressWidth = pdf.getTextWidth(practiceAddress);
      pdf.text(practiceAddress, (pageWidth - addressWidth) / 2, yPos);
      yPos += 5;
    }
    
    if (practicePhone) {
      const phoneText = `Phone: ${practicePhone}`;
      const phoneWidth = pdf.getTextWidth(phoneText);
      pdf.text(phoneText, (pageWidth - phoneWidth) / 2, yPos);
      yPos += 5;
    }
    
    if (practiceEmail) {
      const emailText = `Email: ${practiceEmail}`;
      const emailWidth = pdf.getTextWidth(emailText);
      pdf.text(emailText, (pageWidth - emailWidth) / 2, yPos);
      yPos += 5;
    }
    
    yPos += 5;
    
    // Add a line separator
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    
    // Add form title
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    const titleLines = pdf.splitTextToSize(form.title, maxWidth);
    pdf.text(titleLines, margin, yPos);
    yPos += (titleLines.length * 7) + 5;
    
    // Add category
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'italic');
    pdf.text(`Category: ${form.category}`, margin, yPos);
    yPos += 10;
    
    // Add another separator
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 10;
    
    // Add form content
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    const lines = content.split('\n');
    let isFirstLines = true;
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      // Check if we need a new page
      if (yPos > pdf.internal.pageSize.height - 20) {
        pdf.addPage();
        yPos = 20;
      }
      
      const trimmedLine = line.trim();
      
      if (trimmedLine === '') {
        yPos += 4;
        continue;
      }
      
      // First 2 lines are the title - make them bold, larger, and centered
      if (isFirstLines && i < 2 && !trimmedLine.startsWith('This handout')) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        const wrappedLines = pdf.splitTextToSize(trimmedLine, maxWidth);
        // Center each line
        wrappedLines.forEach((textLine, lineIndex) => {
          const textWidth = pdf.getTextWidth(textLine);
          const xPos = (pageWidth - textWidth) / 2;
          pdf.text(textLine, xPos, yPos + (lineIndex * 6));
        });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        yPos += (wrappedLines.length * 6) + 3;
        continue;
      }
      
      isFirstLines = false;
      
      // Handle checkboxes - replace ■ (BLACK SQUARE) with empty checkbox
      if (trimmedLine.includes('■')) {
        // Split by ■ and process each part
        const parts = trimmedLine.split('■');
        let xOffset = margin;
        
        for (let i = 0; i < parts.length; i++) {
          if (i > 0) {
            // Draw empty checkbox before this part
            pdf.rect(xOffset, yPos - 3, 3, 3);
            xOffset += 5;
          }
          
          if (parts[i].trim()) {
            const partText = parts[i].trim();
            pdf.text(partText, xOffset, yPos);
            xOffset += pdf.getTextWidth(partText) + 2;
          }
        }
        
        yPos += 6;
        continue;
      }
      
      // Handle checkboxes - replace [  ] or [ ] with actual checkbox (fallback)
      const hasCheckbox = trimmedLine.match(/^\[\s*\]/);
      if (hasCheckbox) {
        // Draw a checkbox
        pdf.rect(margin, yPos - 3, 3, 3);
        const textAfterCheckbox = trimmedLine.replace(/^\[\s*\]\s*/, '');
        const wrappedLines = pdf.splitTextToSize(textAfterCheckbox, maxWidth - 5);
        pdf.text(wrappedLines, margin + 5, yPos);
        yPos += (wrappedLines.length * 5) + 2;
        continue;
      }
      
      // Numbered items (1., 2., 3.) - these are BOLD in original PDFs
      if (trimmedLine.match(/^\d+\.\s+/)) {
        pdf.setFont('helvetica', 'bold');
        const wrappedLines = pdf.splitTextToSize(trimmedLine, maxWidth);
        pdf.text(wrappedLines, margin, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += (wrappedLines.length * 5) + 3;
        continue;
      }
      
      // Section headings
      const isMainHeading = 
        trimmedLine.match(/^Common Problems \(.*Emergency\)/i) ||
        trimmedLine.match(/^When to Call the Dentist/i) ||
        trimmedLine.match(/^Warning Signs & When to Call/i);
      
      if (isMainHeading) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        const wrappedLines = pdf.splitTextToSize(trimmedLine, maxWidth);
        pdf.text(wrappedLines, margin, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        yPos += (wrappedLines.length * 6) + 3;
        continue;
      }
      
      // Handle bullet points (lines starting with space/tab are indented bullets)
      if (line.startsWith(' ') || line.startsWith('\t')) {
        const bulletText = trimmedLine.replace(/^[•-]\s*/, '');
        pdf.text('•', margin + 5, yPos);
        const wrappedLines = pdf.splitTextToSize(bulletText, maxWidth - 10);
        pdf.text(wrappedLines, margin + 10, yPos);
        yPos += (wrappedLines.length * 5) + 1;
        continue;
      }
      
      // Handle bold headers (text between ** or lines that look like headers)
      if (trimmedLine.includes('**')) {
        const headerText = trimmedLine.replace(/\*\*/g, '');
        pdf.setFont('helvetica', 'bold');
        const wrappedLines = pdf.splitTextToSize(headerText, maxWidth);
        pdf.text(wrappedLines, margin, yPos);
        pdf.setFont('helvetica', 'normal');
        yPos += (wrappedLines.length * 5) + 3;
        continue;
      }
      
      // Regular text
      const wrappedLines = pdf.splitTextToSize(trimmedLine, maxWidth);
      pdf.text(wrappedLines, margin, yPos);
      yPos += (wrappedLines.length * 5) + 2;
    }
    
    // Add footer
    const totalPages = pdf.internal.pages.length - 1; // -1 because first element is null
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(128, 128, 128);
      const footerText = `${form.title} - Page ${i} of ${totalPages}`;
      const footerWidth = pdf.getTextWidth(footerText);
      pdf.text(footerText, (pageWidth - footerWidth) / 2, pdf.internal.pageSize.height - 10);
      pdf.setTextColor(0, 0, 0);
    }
    
    // Save the PDF
    const fileName = `${form.title.replace(/[^a-z0-9]/gi, '_')}.pdf`;
    pdf.save(fileName);
    
    console.log('✅ Management Form PDF generated:', fileName);
    return true;
    
  } catch (error) {
    console.error('❌ Error generating PDF:', error);
    return false;
  }
};
